import { composeClassNames, universalEach } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import React, { CSSProperties } from "react";
import { Col, Container, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Heading from "src/components/Heading";
import Property from "src/components/Property";
import RichText from "src/components/RichText";
import Translate from "src/components/Translate";
import constants from "src/constants";
import calcDuration from "src/functions/calcDuration";
import gutter from "src/functions/gutter";
import translate from "src/functions/translate";
import { sContentProperties } from "src/styles/sContentProperties.module.scss";
import { sNftAttributes } from "src/styles/sNftAttributes.module.scss";
import { ApiAttributesJson } from "src/types/ApiAttributesJson";
import { ApiVerified } from "src/types/ApiVerified";
import { VariantProperty } from "src/types/VariantProperty";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  benefits?: string;
  contraindications?: string;
  duration?: number | null | undefined;
  difficulty?: string;
  practice_styles?: { id: string; title: string }[];
  practice_skills?: { id: string; title: string }[];
  practice_body_parts?: { id: string; title: string }[];
  practice_equipments?: { id: string; title: string }[];
  practice_focuses?: { id: string; title: string }[];
  verified: ApiVerified;
  nftAttributes?: ApiAttributesJson;
};

function ContentProperties({
  style,
  className,
  benefits,
  contraindications,
  duration,
  difficulty,
  practice_styles,
  practice_skills,
  practice_body_parts,
  practice_equipments,
  practice_focuses,
  verified,
  nftAttributes,
}: Props) {
  const { locale: lang } = useRouter();

  const practice = {
    style: practice_styles,
    skill: practice_skills,
    body_parts: practice_body_parts,
    equipment: practice_equipments,
    focus: practice_focuses,
  };

  function propertyMultiple(param: VariantProperty) {
    let propertiesSerialized = "";
    if (practice && practice[`${param}`]?.length > 0) {
      let practiceList = practice[`${param}`];
      let properties = universalEach(practiceList, (item) => {
        if (item.title !== null) {
          let property = ``;
          param !== `skill`
            ? (property = translate(item?.title?.replace(/_/g, ` `), lang))
            : (property = translate(
                item?.title?.replace(`_`, `: `, 1).replace(/_/g, ` `),
                lang
              ));
          return property;
        }
      });
      propertiesSerialized = properties
        .filter((x) => x !== undefined)
        .sort()
        .join(", ");
      constants.Filter.SkillCategories.map((skill) => {
        propertiesSerialized = propertiesSerialized.replace(
          new RegExp(`${translate(skill, lang)}\:`, "gm"),
          (
            (i) => (m: string) =>
              !i++ ? m : ""
          )(0)
        );
      });
    }
    return (
      propertiesSerialized &&
      propertiesSerialized.length > 0 && (
        <Property
          type={param}
          key={`${param}`}
          size="lg"
          showLabel={true}
          showIcon={true}
          value={propertiesSerialized}
        />
      )
    );
  }

  return (
    <Row
      style={style}
      className={composeClassNames([
        sContentProperties,
        className,
        "video-properties",
      ])}
    >
      {nftAttributes?.attributes ? (
        <Container fluid>
          <Heading type="h4" title={<Translate tKey={"Attributes"} />} />
          <Row>
            <Col xs={12} className={composeClassNames([sNftAttributes])}>
              {nftAttributes.attributes.map((attribute) => {
                return (
                  <div key={attribute.value}>
                    <div className={"property-label"}>
                      <Translate tKey={attribute.trait_type} />:{" "}
                    </div>
                    {attribute.value}
                  </div>
                );
              })}
            </Col>
          </Row>
        </Container>
      ) : (
        <>
          <Col
            xs={{ span: 12, order: 2 }}
            lg={{ span: 7, order: 1 }}
            className={`properties-list`}
          >
            {difficulty ? (
              <Property
                type={`difficulty`}
                key={`difficulty`}
                size="lg"
                showLabel={true}
                showIcon={true}
                value={translate(difficulty.replace(`_`, ` `), lang)}
              />
            ) : null}
            {propertyMultiple(`style`)}
            {propertyMultiple(`focus`)}
            {propertyMultiple(`body_part`)}
            {propertyMultiple(`equipment`)}
            {propertyMultiple(`skill`)}
            {duration > 0 ? (
              <Property
                type={`duration`}
                key={`duration`}
                size="lg"
                showLabel={true}
                showIcon={true}
                value={calcDuration(duration, lang)}
              />
            ) : null}
            {verified && verified.verified ? (
              <Property
                type={`verified`}
                key={`verified`}
                size="lg"
                showLabel={true}
                showIcon={true}
                value={new Date(verified.when).toLocaleDateString(
                  constants.Locales[lang].locale,
                  // @ts-ignore
                  constants.Dates.numeric // TODO: TS error
                )}
              />
            ) : null}
          </Col>
          <Col
            lg={{ span: 5, order: 2 }}
            className={`pros-cons`}
            xs={{ span: 12, order: 1 }}
          >
            {benefits ? (
              <>
                <Heading type="h4" title={<Translate tKey={`Benefits`} />} />
                <RichText size="xn" style={gutter(`margin`, `bottom`, `xm`)}>
                  <ReactMarkdown>{benefits}</ReactMarkdown>
                </RichText>
              </>
            ) : null}
            <Heading
              type="h4"
              title={<Translate tKey={`Contraindications`} />}
            />
            <RichText size="xn">
              {!contraindications ||
              contraindications === "" ||
              contraindications === null ? (
                <Translate tKey="Before using the service, consider your state of health and adapt the intensity of the exercise to your abilities. In case of any health limitations, consult a doctor." />
              ) : (
                <ReactMarkdown>{contraindications}</ReactMarkdown>
              )}
            </RichText>
          </Col>
        </>
      )}
    </Row>
  );
}

export default ContentProperties;
